import React, { useState, useCallback, useEffect } from "react"
import { CircularProgress, AutocompleteProps } from "@mui/material"
import {
  SortDirection,
  LogicalOperator,
  FilterOperator,
  Vendor,
  useGetCitiesOptionsLazyQuery
} from "src/graphql/quark/quark.type"
import InputAutocomplete from "src/shared/components/input-autocomplete"

interface Option {
  id: string
  name: string
}

const SelectCityField: React.FC<
  {
    onChange: (val: string | string[]) => void
    value?: string | string[] | null
    multiple?: boolean
    error?: boolean
    showChip?: "outside" | "inside"
    helperText?: string
    countryId?: string
    vendor?: Vendor
    placeholder?: string
    label?: string
    required?: boolean
  } & Omit<AutocompleteProps<any, any, any, any>, "renderInput" | "options">
> = ({
  onChange,
  value,
  error,
  multiple,
  helperText,
  vendor,
  showChip,
  countryId,
  required,
  label,
  placeholder,
  ...rest
}) => {
  const [options, setOptions] = useState<Option[]>([])
  const [loading, setLoading] = useState(false)
  const [getCityOptions, { error: getError }] = useGetCitiesOptionsLazyQuery()

  useEffect(() => {
    if (value && !options.length) fetchOptions()
  }, [value])
  const fetchOptions = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getCityOptions({
        variables: {
          ...(countryId && {
            filter: {
              conditions: [
                {
                  operator: FilterOperator.Equal,
                  field: "countryId",
                  value: countryId
                }
              ],
              logicalOperator: LogicalOperator.And
            }
          }),
          sort: [
            {
              direction: SortDirection.Asc,
              field: "name"
            }
          ],
          page: 1,
          limit: 100000
        }
      })

      if (!response.data?.getCities.data.length) return null
      const resData: Option[] = response.data?.getCities.data?.map((val: any) => ({
        id: val.id as string,
        name: val.name as string
      }))
      setOptions(resData)
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }, [countryId, setLoading])

  return (
    <InputAutocomplete
      {...rest}
      options={options}
      multiple={multiple}
      getOptionKey={(option) => option?.id ?? ""}
      getOptionLabel={(option) => option.name ?? ""}
      onOpen={() => fetchOptions()}
      loading={loading}
      value={multiple ? options.filter((val) => value?.includes(val.id)) : options.find((val) => val.id === value)}
      onChange={(val: any) => {
        onChange(multiple ? val?.map((val: any) => val.id) : val?.id)
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name}
        </li>
      )}
      showChip={showChip}
      placeholder={placeholder}
      loadingText={<CircularProgress size={20} />}
      error={error ?? !!getError?.message}
      helperText={helperText ?? getError?.message}
      label={label}
      required={required}
    />
  )
}

export default SelectCityField
